import api from './api';

/**
 * EDI LCs (cms.edi_csrs) — a many-to-many LC ↔ EDI handle, presented GROUPED by
 * handle. The grid goes through the /tablemaint engine (key 'edi-csrs', read-only);
 * this service backs the bespoke drawer: the EDI-handle dropdown + shared LC roster,
 * a handle's current LC set, and the set-reconcile save.
 */
export interface Option { value: string; label: string; }

export interface EdiCsrsOptions {
  edihandles: Option[];
  csrs: Option[];
  /** Handles that already have an active mapping — hidden from the Add dropdown. */
  mapped_handles: string[];
}

export async function getEdiCsrsOptions(): Promise<EdiCsrsOptions> {
  const res = await api.get('/edicsrs/form-options');
  return res.data as EdiCsrsOptions;
}

/** Edit-history endpoint for a whole EDI handle (passed to EditHistoryLink). */
export function ediCsrsHistoryEndpoint(edihandle: string): string {
  return `/edicsrs/history?edihandle=${encodeURIComponent(edihandle)}`;
}

export async function getEdiCsrMembers(edihandle: string): Promise<string[]> {
  const res = await api.get('/edicsrs/members', { params: { edihandle } });
  return (res.data?.csrs ?? []) as string[];
}

export async function saveEdiCsrMembers(edihandle: string, csrs: string[]): Promise<void> {
  await api.put('/edicsrs/members', { edihandle, csrs });
}

// ---------------------------------------------------------------------------
// Lane rules (cms.edi_csr_rules) — OPTIONAL narrowing on top of the membership above.
//
// Membership answers "does this LC work this handle". A rule narrows them within it to
// customer(s) / delivery location(s) / move type(s) / bill-to(s), and the MOST SPECIFIC
// matching rule wins — so a narrower rule takes orders AWAY from a broader one rather
// than both LCs seeing them. An LC with membership and NO rules is the handle's
// fallback: they receive whatever no rule claims.
// ---------------------------------------------------------------------------

/** One rule row. Each axis is a comma list; BLANK MEANS ANY (the wildcard). */
export interface EdiLcRule {
  recno: number;            // 0 = not yet saved
  billtolist: string;
  customerlist: string;
  movetypelist: string;
  deliverylist: string;
  /** Count of constrained axes (0-4) — the tier that decides who wins. Server-computed. */
  specificity?: number;
}

export interface EdiLcRuleGroup {
  csr: string;
  rules: EdiLcRule[];
  /** False when the LC holds rules but has lost their edi_csrs membership — an orphan
   *  the resolver ignores, surfaced so it can be cleaned up. */
  member: boolean;
}

export interface EdiCsrRulesResponse {
  edihandle: string;
  lcs: EdiLcRuleGroup[];
  /** True when the handle is only PARTLY lane-scoped. That is the state worth warning
   *  about: the LCs without rules have just become catch-all-only. */
  mixed: boolean;
}

export interface EdiRuleAxisOption { value: number | string; label: string; }

/** Axis choices for one handle, scoped to the lanes it actually receives (the global
 *  locations picker is ~20k rows; a handle has at most a few hundred). */
export interface EdiRuleOptions {
  edihandle: string;
  movetypes: EdiRuleAxisOption[];
  billtos: EdiRuleAxisOption[];
  customers: EdiRuleAxisOption[];
  deliveries: EdiRuleAxisOption[];
}

export async function getEdiRuleOptions(edihandle: string): Promise<EdiRuleOptions> {
  const res = await api.get('/edicsrs/rule-options', { params: { edihandle } });
  return res.data as EdiRuleOptions;
}

export async function getEdiCsrRules(edihandle: string): Promise<EdiCsrRulesResponse> {
  const res = await api.get('/edicsrs/rules', { params: { edihandle } });
  return res.data as EdiCsrRulesResponse;
}

/**
 * Reconcile the handle's whole rule set: rows with a recno update, rows without insert,
 * and anything live but not submitted is soft-deleted.
 *
 * MUST be called after saveEdiCsrMembers — the server rejects a rule for an LC with no
 * active membership, since both the ownership clause and the fallback start from
 * edi_csrs. It also rejects a rule with all four axes blank: that would be tier 0,
 * matching everything and therefore winning only when nothing narrower does, which is
 * already what having no rule means.
 */
export async function saveEdiCsrRules(
  edihandle: string,
  lcs: { csr: string; rules: EdiLcRule[] }[],
): Promise<void> {
  await api.put('/edicsrs/rules', { edihandle, lcs });
}
